import React, { useEffect, useState } from 'react';
import {
  AllowedProvidersUrl,
  findOembedProviderUrl,
  isValidProvider,
} from '@loyalty/shared-common';
import { Box, Button, DialogActions, DialogContent, MenuItem, TextField } from '@mui/material';
import { EmbeddedMediaComponentAttributes, SpotifySize } from '@loyalty/editor-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { QlfFormRow, colorPalette } from '@qualifio/ui-commons';
import { faSpotify } from '@fortawesome/free-brands-svg-icons/faSpotify';
import { faYoutube } from '@fortawesome/free-brands-svg-icons/faYoutube';
import { useTranslation } from 'react-i18next';
import { EditorModalContentProps } from '../../../components/EditorModal';
import { Title } from '../../../../shared/layout/Title';

export interface EditorModalProps extends EditorModalContentProps {
  callback: (data: EmbeddedMediaComponentAttributes) => void;
  data: EmbeddedMediaComponentAttributes;
}

export function EditorEmbeddedMediaModal({
  callback,
  data,
  closeModal,
}: EditorModalProps): React.ReactElement {
  const { t } = useTranslation();
  const [targetUrl, setTargetUrl] = useState(data?.targetUrl);
  const [spotifySize, setSpotifySize] = useState(data?.spotifySize);
  const [error, setError] = useState<string>();
  const [isDirty, setIsDirty] = useState<boolean>(false);

  useEffect(() => {
    setTargetUrl(data?.targetUrl);
    setSpotifySize(data?.spotifySize);
  }, [data]);

  const providerUrl = findOembedProviderUrl(targetUrl);
  const isSpotify = providerUrl === AllowedProvidersUrl.SPOTIFY;

  return (
    <>
      <DialogContent sx={{ textAlign: 'left !important' }}>
        <Box textAlign="center">
          <Title>{t('editor.embedded-media.modal.title')}</Title>
        </Box>

        <QlfFormRow
          label={t('editor.embedded-media.modal.target-url.label')}
          labelWidth={100}
          mandatory
        >
          <TextField
            value={targetUrl}
            placeholder={t('editor.embedded-media.modal.target-url.placeholder')}
            onChange={e => {
              const url = e.target.value;
              setIsDirty(true);
              if (!url) {
                setError(t('editor.embedded-media.trait.target-url.required'));
              } else if (!isValidProvider(url)) {
                setError(t('editor.embedded-media.modal.target-url.error'));
              } else {
                setError(undefined);
              }
              setTargetUrl(url);
            }}
            error={!!error}
            helperText={error}
          />
        </QlfFormRow>

        {isSpotify && (
          <QlfFormRow label={'Size '} labelWidth={100}>
            <TextField
              select
              value={spotifySize}
              onChange={e => {
                setIsDirty(true);
                setSpotifySize(e.target.value as SpotifySize);
              }}
              defaultValue={SpotifySize.SMALL}
            >
              <MenuItem value={SpotifySize.SMALL}>
                {t('editor.embedded-media.traits.spotify.size.small')}
              </MenuItem>
              <MenuItem value={SpotifySize.MEDIUM}>
                {t('editor.embedded-media.traits.spotify.size.medium')}
              </MenuItem>
              <MenuItem value={SpotifySize.BIG}>
                {t('editor.embedded-media.traits.spotify.size.big')}
              </MenuItem>
            </TextField>
          </QlfFormRow>
        )}
        <Box
          textAlign="left"
          sx={{ fontSize: '12px', fontWeight: 400, color: colorPalette.grey.mainHover }}
        >
          {t('editor.embedded-media.modal.included-services')}
        </Box>
        <FontAwesomeIcon
          icon={faYoutube}
          color={colorPalette.red.main}
          style={{ marginRight: '5px' }}
        />
        <FontAwesomeIcon
          icon={faSpotify}
          color={colorPalette.green.dark}
          style={{ marginRight: '5px' }}
        />
        <a
          href="https://support.qualifio.com/hc/en-us/articles/7384992405916"
          target="_blank"
          style={{ color: colorPalette.blue.main, fontSize: '12px', textDecoration: 'none' }}
          rel="noreferrer"
        >
          {t('editor.embedded-media.modal.link.support')}
        </a>
      </DialogContent>

      <DialogActions>
        <Button
          type="button"
          color="grey"
          onClick={() => {
            setError(undefined);
            closeModal();
          }}
        >
          {t('generic.actions.cancel')}
        </Button>
        <Button
          color="success"
          disabled={!isDirty}
          onClick={() => {
            if (!error) {
              callback({ targetUrl, spotifySize });
              setError(undefined);
              closeModal();
            }
          }}
        >
          {t('editor.embedded-media.modal.apply')}
        </Button>
      </DialogActions>
    </>
  );
}
